import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { useCloseOnOutsideDialog } from "./useCloseOnOutsideDialog";
import { useDebouncedEffect } from "../../common-deprecated/hooks";

type Props = {
    open: boolean;
    close?: () => void;
    closeOnOutsideClick?: boolean;
    // When true, the browsers back button will also close the modal.
    // This is used for closing the modal with the retailerscreens back CTA in the navigation bar.
    closeOnBackButton?: boolean;
};

type UseModalReturnType = {
    ref: MutableRefObject<HTMLDialogElement | null>;
    closeModal: () => void;
};

const MODAL_POP_STATE = "modal";

const useModal = ({
    open,
    close,
    closeOnOutsideClick = true,
    closeOnBackButton = false,
}: Props): UseModalReturnType => {
    const ref = useRef<HTMLDialogElement | null>(null);

    // Default open / close logic
    useEffect(() => {
        if (open && !ref?.current?.open) ref?.current?.showModal();
        else if (!open && ref?.current?.open) ref.current?.close();
    }, [open, ref]);

    // Returned function to close the modal manually
    const closeModal = useCallback(() => {
        close?.();
        ref?.current?.close();
    }, [close, ref]);

    // Handle browser back button if explicitly enabled via the props.
    useDebouncedEffect(
        () => {
            if (closeOnBackButton) window.history.pushState(MODAL_POP_STATE, document.title, window.location.href);

            if (closeOnBackButton && close) window.addEventListener("popstate", close);

            return () => {
                if (closeOnBackButton && close) {
                    // If the modal isn't unmounted via the browsers back button, the top history state will still be present, remove it accordingly:
                    if (window.history.state === MODAL_POP_STATE) window.history.back();
                    window.removeEventListener("popstate", close);
                }
            };
        },
        10, // Debounce of 10ms to prevent unnecessary unmounts.
        [closeOnBackButton, close],
    );

    // If closeOnOutsideclick is false, this logic will be disabled at all times
    useCloseOnOutsideDialog(ref, closeModal, closeOnOutsideClick ? !open : true);

    return { ref, closeModal };
};

export default useModal;
